/*
 * section-fp-v2-collection.js — Page collection 2026 (sections/fp-v2-collection.liquid)
 * ─────────────────────────────────────────────────────────────────────────────
 * - Facets : un seul menu ouvert à la fois, fermeture au clic extérieur / Échap.
 *   Tout changement (case, tri, prix) re-rend la section via `?section_id=` et
 *   remplace [data-fpv2c-root] ; l'URL est mise à jour (pushState) et le menu
 *   qui était ouvert est rouvert après le remplacement.
 * - Cards : onglets variantes → prix / image / dispo du bouton ; « Acheter » →
 *   POST /cart/add.js. Le side cart (bnz-cart-drawer.js) intercepte ce fetch,
 *   se rafraîchit et s'ouvre : rien d'autre à faire ici.
 * - Notes : étoiles + nombre d'avis recalés sur le bottomline Yotpo (cf. plus bas).
 * Délégation d'événements sur document : survit aux remplacements AJAX.
 */
(() => {
  if (window.__fpv2CollectionInit) return;
  window.__fpv2CollectionInit = true;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let controller = null;
  let priceTimer = null;

  /* ─── Facets ──────────────────────────────────────────────────────────── */

  const closeFacets = (scope, except) => {
    scope.querySelectorAll('details.fpv2c-facet[open]').forEach((d) => {
      if (d !== except) d.open = false;
    });
  };

  const buildQuery = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    return params.toString();
  };

  const render = async (section, search, { push = true } = {}) => {
    const sid = section.dataset.sectionId;
    const openFacet = section.querySelector('details.fpv2c-facet[open]');
    const reopen = openFacet ? openFacet.dataset.fpv2cFacet : null;

    if (controller) controller.abort();
    controller = new AbortController();
    section.classList.add('is-loading');

    const query = search.replace(/^\?/, '');
    const url = `${window.location.pathname}?${query ? query + '&' : ''}section_id=${encodeURIComponent(sid)}`;

    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-fpv2c-root]');
      const current = section.querySelector('[data-fpv2c-root]');
      if (!fresh || !current) throw new Error('missing root');
      current.replaceWith(fresh);
      watchRatings(fresh);

      if (reopen) {
        const d = fresh.querySelector(`details.fpv2c-facet[data-fpv2c-facet="${CSS.escape(reopen)}"]`);
        if (d) d.open = true;
      }
      if (push) {
        const next = `${window.location.pathname}${query ? '?' + query : ''}`;
        window.history.pushState({ fpv2c: true }, '', next);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Repli : navigation classique
      window.location.search = query;
    } finally {
      section.classList.remove('is-loading');
    }
  };

  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.classList.contains('fpv2c-facet') || !d.open) return;
    const section = d.closest('[data-fpv2c]');
    if (section) closeFacets(section, d);
  }, true);

  document.addEventListener('click', (e) => {
    // Tête du panneau (recouvre la pastille) → ferme le menu
    const head = e.target.closest('.fpv2c-panel__head');
    if (head) {
      const d = head.closest('details');
      if (d) d.open = false;
      return;
    }
    // Clic hors d'un menu → ferme tout
    if (!e.target.closest('details.fpv2c-facet')) {
      document.querySelectorAll('[data-fpv2c] details.fpv2c-facet[open]').forEach((d) => { d.open = false; });
    }
    // Liens internes (reset, pagination) → AJAX
    const link = e.target.closest('a[data-fpv2c-link]');
    if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      const section = link.closest('[data-fpv2c]');
      if (!section) return;
      e.preventDefault();
      const target = new URL(link.href, window.location.origin);
      render(section, target.search).then(() => {
        if (link.closest('.fpv2c-pagination')) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('[data-fpv2c] details.fpv2c-facet[open]');
    if (!open) return;
    open.open = false;
    const summary = open.querySelector('summary');
    if (summary) summary.focus();
  });

  document.addEventListener('change', (e) => {
    const form = e.target.closest('form[data-fpv2c-form]');
    if (!form) return;
    const section = form.closest('[data-fpv2c]');
    if (!section) return;
    clearTimeout(priceTimer);
    render(section, buildQuery(form));
  });

  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-fpv2c-price]')) return;
    const form = e.target.closest('form[data-fpv2c-form]');
    const section = form && form.closest('[data-fpv2c]');
    if (!section) return;
    clearTimeout(priceTimer);
    priceTimer = setTimeout(() => render(section, buildQuery(form)), 600);
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-fpv2c-form]');
    if (!form) return;
    const section = form.closest('[data-fpv2c]');
    if (!section) return;
    e.preventDefault();
    render(section, buildQuery(form));
  });

  window.addEventListener('popstate', () => {
    const section = document.querySelector('[data-fpv2c]');
    if (section) render(section, window.location.search, { push: false });
  });

  /* ─── Cards : variantes ───────────────────────────────────────────────── */

  const setBuyState = (card, { price, comparePrice, available }) => {
    const btn = card.querySelector('[data-fpv2c-buy]');
    if (!btn) return;
    const text = btn.querySelector('[data-fpv2c-buy-text]') || btn;
    btn.disabled = !available;
    if (!available) {
      text.textContent = btn.dataset.soldOut;
      return;
    }
    text.textContent = `${btn.dataset.label} `;
    if (comparePrice) {
      const s = document.createElement('s');
      s.className = 'fpv2-pcard__compare';
      s.textContent = comparePrice;
      text.append(s, ' ');
    }
    text.append(price);
  };

  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-fpv2c-variant]');
    if (!tab) return;
    const card = tab.closest('[data-fpv2c-card]');
    if (!card) return;
    e.preventDefault();

    card.querySelectorAll('[data-fpv2c-variant]').forEach((t) => t.setAttribute('aria-checked', String(t === tab)));
    card.dataset.variantId = tab.dataset.fpv2cVariant;
    setBuyState(card, {
      price: tab.dataset.price,
      comparePrice: tab.dataset.comparePrice,
      available: tab.dataset.available === 'true',
    });

    const img = card.querySelector('.fpv2-pcard__img');
    if (img && tab.dataset.image) {
      img.src = tab.dataset.image;
      if (tab.dataset.srcset) img.srcset = tab.dataset.srcset;
    }
  });

  /* ─── Cards : vidéo de survol ─────────────────────────────────────────── */
  // preload="none" : la vidéo n'est chargée qu'au premier survol, lue tant que
  // le pointeur est sur la card, remise au début à la sortie.
  const hoverVideo = (e) => {
    const card = e.target instanceof Element && e.target.matches('[data-fpv2c-card]') ? e.target : null;
    const video = card && card.querySelector('[data-fpv2c-hover-video]');
    if (!video) return;
    if (e.type === 'mouseenter') {
      video.play().catch(() => {});
    } else {
      video.pause();
      video.currentTime = 0;
    }
  };
  document.addEventListener('mouseenter', hoverVideo, true);
  document.addEventListener('mouseleave', hoverVideo, true);

  /* ─── Cards : paires de packs « X » / « X+ » ──────────────────────────── */
  // Les deux cards sont dans la même cellule ; l'onglet affiche l'autre card
  // et y replace le focus (sinon il resterait sur un élément masqué).
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-fpv2c-pair-tab]');
    if (!tab) return;
    const cell = tab.closest('[data-fpv2c-pair]');
    if (!cell) return;
    e.preventDefault();
    const target = tab.dataset.fpv2cPairTab;
    const next = cell.querySelector(`[data-fpv2c-pair-card="${target}"]`);
    if (!next || !next.hidden) return;
    const hadFocus = cell.contains(document.activeElement);
    cell.querySelectorAll('[data-fpv2c-pair-card]').forEach((card) => { card.hidden = card !== next; });
    if (hadFocus) {
      const nextTab = next.querySelector(`[data-fpv2c-pair-tab="${target}"]`);
      if (nextTab) nextTab.focus();
    }
  });

  // Flèches gauche/droite dans le radiogroup des variantes / paires
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const pairTab = e.target.closest('[data-fpv2c-pair-tab]');
    if (pairTab) {
      const other = pairTab.parentElement.querySelector('[data-fpv2c-pair-tab]:not([aria-checked="true"])');
      e.preventDefault();
      if (other) other.click();
      return;
    }
    const tab = e.target.closest('[data-fpv2c-variant]');
    if (!tab) return;
    const tabs = Array.from(tab.parentElement.querySelectorAll('[data-fpv2c-variant]'));
    const i = tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1);
    const next = tabs[(i + tabs.length) % tabs.length];
    e.preventDefault();
    next.focus();
    next.click();
  });

  /* ─── Cards : ajout panier ────────────────────────────────────────────── */

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-fpv2c-buy]');
    if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
    const card = btn.closest('[data-fpv2c-card]');
    if (!card) return;
    e.preventDefault();

    const text = btn.querySelector('[data-fpv2c-buy-text]') || btn;
    // Nœuds clonés : le libellé contient le prix barré (<s>), textContent l'aplatirait.
    const original = Array.from(text.childNodes, (n) => n.cloneNode(true));
    const restore = () => text.replaceChildren(...original);
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(card.dataset.variantId), quantity: 1 }] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.description || data.message) {
          text.textContent = data.description || data.message;
          setTimeout(restore, 2500);
        }
      }
    } catch (err) {
      restore();
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  });

  /* ─── Notes Yotpo des cards ───────────────────────────────────────────────
   * Le Liquid rend la note depuis les metafields Yotpo, qui décrochent (ex.
   * pack-night-script : 1 avis contre 47 publiés). On la recale sur le
   * bottomline Yotpo, mêmes chiffres que la liste d'avis de la fiche : une
   * requête CDN par produit (mémorisée), lancée quand la card approche de
   * l'écran. On observe la card et non la note : une note masquée (aucun
   * metafield) n'intersecte jamais. Remplissage au quart d'étoile inférieur,
   * comme le Liquid : 5 étoiles pleines = 5,0 pile. */
  const bottomlines = new Map();

  const fetchBottomline = (key, id) => {
    const cacheKey = `${key}:${id}`;
    if (!bottomlines.has(cacheKey)) {
      const url = `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(key)}/products/${encodeURIComponent(id)}/bottomline`;
      bottomlines.set(cacheKey, fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(res.status);
          return res.json();
        })
        .then((data) => {
          const bl = data && data.response && data.response.bottomline;
          if (!bl) throw new Error('no bottomline');
          return { average: Number(bl.average_score) || 0, total: Number(bl.total_review) || 0 };
        })
        .catch((err) => {
          bottomlines.delete(cacheKey); /* échec : la prochaine card retentera */
          throw err;
        }));
    }
    return bottomlines.get(cacheKey);
  };

  const applyRating = (el, bl) => {
    if (!bl.total) {
      el.hidden = true;
      return;
    }
    const display = (Math.round(bl.average * 10) / 10).toFixed(1);
    const stars = el.querySelector('[data-fpv2-stars]');
    const count = el.querySelector('[data-fpv2c-rating-count]');
    if (stars) stars.style.setProperty('--fpv2-stars-value', String(Math.min(20, Math.floor(bl.average * 4)) / 4));
    if (count) count.textContent = `(${bl.total})`;
    const tpl = el.dataset.ariaTpl;
    if (tpl) el.setAttribute('aria-label', tpl.split('[[r]]').join(display).split('[[c]]').join(String(bl.total)));
    el.hidden = false;
  };

  const hydrateRating = (card) => {
    const el = card.querySelector('[data-fpv2c-rating]');
    const section = card.closest('[data-yotpo-key]');
    if (!el || !section || !section.dataset.yotpoKey) return;
    /* Échec réseau : le rendu serveur reste en place. */
    fetchBottomline(section.dataset.yotpoKey, el.dataset.fpv2cRating)
      .then((bl) => applyRating(el, bl))
      .catch(() => {});
  };

  const ratingObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        hydrateRating(entry.target);
      });
    }, { rootMargin: '300px 0px' })
    : null;

  const watchRatings = (scope) => {
    scope.querySelectorAll('[data-fpv2c-rating]').forEach((el) => {
      const card = el.closest('[data-fpv2c-card]');
      if (!card) return;
      if (ratingObserver) ratingObserver.observe(card);
      else hydrateRating(card);
    });
  };

  watchRatings(document);
  document.addEventListener('shopify:section:load', (e) => watchRatings(e.target));
})();
